import React from "react";
import { Reveal, Eyebrow } from "../components/ui";

const sections = [
  {
    title: "1. Acceptance of Terms",
    body: "By creating an account, you agree to comply with these Terms and our Privacy Policy. We reserve the right to update these terms at any time, and your continued use constitutes acceptance of those changes.",
  },
  {
    title: "2. Eligibility",
    body: "You must be at least 18 years old to create an account. By using ConnectPlus, you represent and warrant that you have the right, authority, and capacity to enter into this agreement.",
  },
  {
    title: "3. Your Account",
    body: "You are responsible for maintaining the confidentiality of your login credentials. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate.",
  },
  {
    title: "4. Acceptable Use and Community Guidelines",
    body: "Your use of the platform is subject to our Community Guidelines. You agree not to: Harass, bully, or abuse other users; Post fraudulent, misleading, or illegal content; Use the platform for spam, scams, or unauthorized commercial promotions; Share another user's personal information without consent; Violate any local, national, or international laws.",
  },
  {
    title: "5. Subscriptions and Payments",
    body: "Certain features of ConnectPlus may require a paid subscription. Payments are processed securely via mobile money (Airtel Money, Mpamba) or bank transfer. Subscription fees are non-refundable, except where required by law. We reserve the right to change subscription pricing at any time.",
  },
  {
    title: "6. User Content",
    body: "You retain ownership of the photos, text, and information you post. However, by posting content, you grant Max & Sherry a non-exclusive, royalty-free license to use, display, and distribute that content to provide the service. You must not post content that infringes on anyone else's copyright or privacy.",
  },
  {
    title: "7. Safety and Interactions",
    body: "While we offer verification and curation, Max & Sherry does not conduct background checks on all users. You are solely responsible for your interactions with other members. Always exercise caution when meeting someone in person, preferably meeting in public spaces.",
  },
  {
    title: "8. Termination",
    body: "We reserve the right to suspend or permanently terminate your account if you violate these Terms or our Community Guidelines. You may delete your account at any time via the Delete Account page.",
  },
  {
    title: "9. Limitation of Liability",
    body: "Max & Sherry ConnectPlus is provided 'as is.' To the maximum extent permitted by law, we are not liable for any indirect, incidental, or consequential damages arising from your use of the platform or interactions with other users.",
  },
];

const toc = [
  ...sections.map((s, i) => ({ id: `terms-${i + 1}`, label: s.title })),
  { id: "contact", label: "10. Contact" },
];

function goTo(e: React.MouseEvent, id: string) {
  e.preventDefault();
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function TermsOfUse() {
  return (
    <main className="bg-white px-[5%] pt-[140px] pb-20 lg:pb-[100px]">
      <div className="max-w-[1100px] mx-auto">
        <Reveal>
          <Eyebrow>Legal</Eyebrow>
        </Reveal>
        <Reveal delay={100}>
          <h1 className="text-[clamp(28px,4vw,44px)] font-bold tracking-[-1.2px] mb-4">
            Terms of Use
          </h1>
        </Reveal>
        <Reveal delay={150}>
          <p className="text-sm text-muted mb-12">Last updated: October 2026</p>
        </Reveal>

        <div className="lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-16">
          {/* Sticky table of contents (desktop only) */}
          <aside className="hidden lg:block">
            <div className="sticky top-[120px]">
              <p className="text-[13px] font-semibold text-black mb-3">
                On this page
              </p>
              <nav className="border-l border-black/10 max-h-[calc(100vh-180px)] overflow-y-auto">
                {toc.map((t) => (
                  <a
                    key={t.id}
                    href={`#${t.id}`}
                    onClick={(e) => goTo(e, t.id)}
                    className="block -ml-px pl-4 py-1.5 text-[13px] text-muted border-l border-transparent hover:text-rose hover:border-rose transition-colors duration-200 no-underline"
                  >
                    {t.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="max-w-[760px]">
            <Reveal delay={175}>
              <div className="mb-12 pb-10 border-b border-black/10">
                <p className="text-[15px] text-muted leading-relaxed">
                  Welcome to Max & Sherry ConnectPlus. By accessing or using our
                  platform, you agree to be bound by these Terms of Use. If you
                  do not agree, please do not use the service.
                </p>
              </div>
            </Reveal>

            {sections.map((s, i) => (
              <Reveal key={s.title} delay={i * 20}>
                <div id={`terms-${i + 1}`} className="mb-8 scroll-mt-[120px]">
                  <h2 className="text-lg font-bold tracking-tight mb-2">
                    {s.title}
                  </h2>
                  <p className="text-[15px] text-muted leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}

            <Reveal>
              <div
                id="contact"
                className="mt-12 pt-8 border-t border-black/10 scroll-mt-[120px]"
              >
                <h2 className="text-lg font-bold tracking-tight mb-2">
                  10. Contact
                </h2>
                <p className="text-[15px] text-muted leading-relaxed">
                  For legal inquiries regarding these Terms, please contact us
                  at{" "}
                  <a
                    href="mailto:legal@maxandsherry.mw"
                    className="text-rose hover:underline"
                  >
                    legal@maxandsherry.mw
                  </a>
                  .
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </main>
  );
}
